import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import AttachmentThumb from "./AttachmentThumb";
import { fmtDate, fmtNum } from "../lib/format";

const locationLabel = (purchase) => {
  if (purchase.receiving_location === "STORE_CLEAN") {
    return "Store Clean";
  }
  if (purchase.receiving_location === "A_STORE") {
    return "Asset Store";
  }
  if (purchase.receiving_location === "A_ROOM") {
    return purchase.room_number ? `Room ${purchase.room_number}` : "Room";
  }
  if (purchase.receiving_location === "A_DEPARTMENT") {
    return purchase.department_name || "Department";
  }
  return purchase.receiving_location || "—";
};

const detailValue = (value) => value || "—";

export default function PurchaseDetailsDialog({
  open,
  onOpenChange,
  purchase,
  endpoint,
  testid,
}) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !purchase?.id) {
      return;
    }
    setDetail(null);
    setError("");
    api
      .get(`${endpoint}/${purchase.id}`)
      .then(({ data }) => setDetail(data))
      .catch((requestError) => {
        setError(formatApiErrorDetail(requestError.response?.data?.detail));
      });
  }, [open, purchase?.id, endpoint]);

  const current = detail || purchase;
  const attachment = current?.attachment_url || current?.attachment;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid={`${testid}-details-dialog`}
      >
        <DialogHeader>
          <DialogTitle data-testid={`${testid}-details-title`}>
            Purchase Details {current?.number ? `— ${current.number}` : ""}
          </DialogTitle>
        </DialogHeader>

        {error ? (
          <div
            className="text-sm text-red-700"
            data-testid={`${testid}-details-error`}
          >
            {error}
          </div>
        ) : !detail ? (
          <div
            className="text-sm text-slate-500"
            data-testid={`${testid}-details-loading`}
          >
            Loading bill details…
          </div>
        ) : (
          <div className="space-y-5" data-testid={`${testid}-details-content`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-slate-500">Bill / Invoice Number</div>
                <div
                  className="font-medium"
                  data-testid={`${testid}-bill-number`}
                >
                  {detailValue(current.bill_number)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Reference / PO Number</div>
                <div
                  className="font-medium"
                  data-testid={`${testid}-po-number`}
                >
                  {detailValue(current.po_number)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Bill Date</div>
                <div
                  className="font-medium"
                  data-testid={`${testid}-bill-date`}
                >
                  {detailValue(current.bill_date)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Entry Date</div>
                <div
                  className="font-medium"
                  data-testid={`${testid}-entry-date`}
                >
                  {fmtDate(current.entry_date)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Supplier</div>
                <div className="font-medium" data-testid={`${testid}-supplier`}>
                  {detailValue(current.supplier || current.vendor_name)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Receive Into</div>
                <div
                  className="font-medium"
                  data-testid={`${testid}-receive-location`}
                >
                  {locationLabel(current)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Entry Status</div>
                <Badge data-testid={`${testid}-entry-status`}>
                  {current.status || "—"}
                </Badge>
              </div>
              <div>
                <div className="text-slate-500">Attachment</div>
                <div data-testid={`${testid}-attachment`}>
                  {attachment ? (
                    <AttachmentThumb
                      url={attachment}
                      testid={`${testid}-attachment-link`}
                    />
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table
                className="vr-table"
                data-testid={`${testid}-details-lines`}
              >
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>UOM</th>
                    <th className="num">Quantity</th>
                    <th className="num">Unit Price</th>
                    <th className="num">Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(current.lines || []).map((line, index) => (
                    <tr key={`${line.item_id}-${index}`}>
                      <td>{line.item_name || line.item_id}</td>
                      <td>{line.unit || "—"}</td>
                      <td className="num">{fmtNum(line.qty)}</td>
                      <td className="num">{fmtNum(line.rate)}</td>
                      <td className="num">
                        {fmtNum(line.line_total ?? line.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-slate-500">Total Quantity</div>
                <div
                  className="font-semibold"
                  data-testid={`${testid}-total-quantity`}
                >
                  {fmtNum(current.total_qty)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Total Bill Value</div>
                <div
                  className="font-semibold"
                  data-testid={`${testid}-total-value`}
                >
                  {fmtNum(current.total_bill_value)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Remarks</div>
                <div data-testid={`${testid}-remarks`}>
                  {detailValue(current.remarks)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm border-t pt-4">
              <div>
                <div className="text-slate-500">Created By</div>
                <div data-testid={`${testid}-created-by`}>
                  {detailValue(current.created_by_name)} ·{" "}
                  {fmtDate(current.created_at)}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Last Edited</div>
                <div data-testid={`${testid}-last-edited`}>
                  {current.last_edited_by_name
                    ? `${current.last_edited_by_name} · ${fmtDate(current.last_edited_at)}`
                    : "Not edited"}
                </div>
              </div>
            </div>

            {(current.edit_history?.length || current.reversal_history?.length ||
              current.operation_history?.length) ? (
              <div className="border-t pt-4 space-y-4" data-testid={`${testid}-correction-history`}>
                <div className="font-semibold text-slate-800">Correction &amp; Reversal History</div>
                {(current.edit_history || []).slice().reverse().map((entry, index) => (
                  <div
                    key={`edit-${entry.edited_at}-${index}`}
                    className="border border-slate-200 p-3 text-sm"
                    data-testid={`${testid}-edit-history-${index}`}
                  >
                    <div className="font-medium">Correction: {entry.reason}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {entry.edited_at} · {entry.edited_by_name} · {entry.operation_id || "legacy"}
                    </div>
                    <div className="grid grid-cols-1 gap-2 mt-3 md:grid-cols-2">
                      <pre className="overflow-x-auto bg-slate-50 p-2 text-xs">Before: {JSON.stringify(entry.before)}</pre>
                      <pre className="overflow-x-auto bg-slate-50 p-2 text-xs">After: {JSON.stringify(entry.after)}</pre>
                    </div>
                  </div>
                ))}
                {(current.reversal_history || []).slice().reverse().map((entry, index) => (
                  <div
                    key={`reverse-${entry.reversed_at}-${index}`}
                    className="border border-red-200 bg-red-50 p-3 text-sm"
                    data-testid={`${testid}-reversal-history-${index}`}
                  >
                    <div className="font-medium">Reversal: {entry.reason}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {entry.reversed_at} · {entry.reversed_by_name} · {entry.operation_id}
                    </div>
                  </div>
                ))}
                {(current.operation_history || []).map((operation) => (
                  <div
                    key={operation.operation_id}
                    className="border border-slate-200 p-3 text-sm"
                    data-testid={`${testid}-operation-${operation.operation_id}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{operation.kind}</span>
                      <Badge>{operation.status}</Badge>
                      <span className="font-mono text-xs">{operation.operation_id}</span>
                    </div>
                    {operation.error && <div className="mt-2 text-red-700">{operation.error}</div>}
                    {!!operation.steps?.length && (
                      <div className="mt-2 text-xs text-slate-600">
                        {operation.steps.map((step) => step.name).join(" → ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" data-testid={`${testid}-details-close`}>
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
